import React, { useState } from 'react';
import KeywordFooter from '../components/KeywordFooter';

const COPYRIGHT_EMAIL = 'jimmy.lagattuta@gmail.com';

const CopyrightPolicy = () => {
  const effectiveDate = 'May 13, 2026';
  const [copiedEmail, setCopiedEmail] = useState(false);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(COPYRIGHT_EMAIL);
      setCopiedEmail(true);

      window.setTimeout(() => {
        setCopiedEmail(false);
      }, 2200);
    } catch (error) {
      console.error('Unable to copy copyright email:', error);
      setCopiedEmail(false);
    }
  };

  return (
    <>
      <main className="min-h-screen bg-slate-950 px-6 py-16 text-white">
        <section className="mx-auto max-w-4xl rounded-3xl border border-cyan-300/20 bg-slate-900/70 p-6 shadow-2xl shadow-cyan-950/30 sm:p-8">
          <p className="text-sm font-bold uppercase tracking-[0.3em] text-cyan-300">
            SubtitleTsunami
          </p>

          <h1 className="mt-3 text-3xl font-black text-white sm:text-4xl">
            Copyright Policy
          </h1>

          <p className="mt-3 text-sm font-bold text-slate-400">
            Effective Date: {effectiveDate}
          </p>

          <p className="mt-6 text-sm leading-7 text-slate-300">
            SubtitleTsunami respects intellectual property rights. This Copyright
            Policy explains the rules for uploading content, how copyright
            concerns can be reported, and how SubtitleTsunami may respond to
            alleged infringement.
          </p>

          <p className="mt-4 text-sm leading-7 text-slate-300">
            SubtitleTsunami is intended only for original, licensed, or
            authorized content. Users may only upload videos they own or have
            permission to upload, subtitle, translate, process, preview, and
            download.
          </p>

          <div className="mt-8 space-y-8">
            <section>
              <h2 className="text-xl font-black text-white">
                1. Authorized Content Only
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                By uploading a video to SubtitleTsunami, you confirm that you
                own the content or have the necessary rights, licenses,
                permissions, and consents to upload, subtitle, translate,
                process, preview, and download that content.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                You are responsible for making sure your use of SubtitleTsunami
                does not infringe copyrights, trademarks, privacy rights,
                publicity rights, contractual rights, platform terms, or any
                other rights of another person or organization.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                2. Prohibited Copyright Activity
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                You may not use SubtitleTsunami to upload, process, subtitle,
                translate, preview, download, distribute, or repurpose content
                that you do not have permission to use.
              </p>

              <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-7 text-slate-300">
                <li>
                  Do not upload movies, television clips, music videos, sports
                  broadcasts, paid course content, podcast clips, creator videos,
                  social media videos, or other copyrighted materials unless you
                  have permission.
                </li>
                <li>
                  Do not use SubtitleTsunami to remove, bypass, alter, obscure,
                  or misuse copyright notices, watermarks, attribution, platform
                  restrictions, or rights-management information.
                </li>
                <li>
                  Do not use SubtitleTsunami to create unauthorized translated,
                  subtitled, edited, redistributed, or derivative versions of
                  another person’s content.
                </li>
                <li>
                  Do not use SubtitleTsunami in a way that violates the terms of
                  YouTube, TikTok, Instagram, X, Facebook, Vimeo, course
                  platforms, streaming platforms, or any other third-party
                  platform.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                3. Upload Confirmation Records
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                SubtitleTsunami may require users to confirm that they own or
                have permission to use a video before uploading it for
                processing.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                We may store records related to that confirmation, including the
                account associated with the upload, original file name, video
                duration, upload time, confirmation status, confirmation time,
                processing event, and IP address associated with the request.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                These records may be used to enforce our Terms of Service,
                investigate misuse, respond to copyright concerns, protect the
                service, and comply with legal, payment processor, hosting,
                infrastructure, or platform requirements.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                4. Temporary Processing Does Not Grant Rights
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                Processing a file through SubtitleTsunami does not give you
                ownership of the original content and does not grant you any
                rights you did not already have.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                If you upload someone else’s content without authorization, the
                fact that SubtitleTsunami generated subtitles, translated text,
                created a preview, or produced a downloadable file does not make
                your use legal or authorized.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                5. Reporting Copyright Concerns
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                If you believe content processed through SubtitleTsunami
                infringes your copyright, please contact us with enough
                information to review the issue.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                Your notice should include:
              </p>

              <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-7 text-slate-300">
                <li>Your full name and contact information.</li>
                <li>
                  A description of the copyrighted work you believe was
                  infringed.
                </li>
                <li>
                  Information reasonably sufficient to identify the material or
                  activity you are concerned about.
                </li>
                <li>
                  A statement that you have a good-faith belief that the use of
                  the material is not authorized by the copyright owner, its
                  agent, or the law.
                </li>
                <li>
                  A statement that the information in your notice is accurate.
                </li>
                <li>
                  A statement that you are the copyright owner or are authorized
                  to act on behalf of the copyright owner.
                </li>
              </ul>

              <div className="mt-4 rounded-2xl border border-cyan-300/15 bg-slate-950/60 px-4 py-3">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-cyan-300/80">
                  Copyright Contact
                </p>

                <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                  <p className="break-all text-sm font-extrabold text-cyan-100">
                    {COPYRIGHT_EMAIL}
                  </p>

                  <button
                    type="button"
                    onClick={handleCopyEmail}
                    className="inline-flex w-fit items-center justify-center rounded-xl border border-cyan-300/20 bg-cyan-300/10 px-3 py-1.5 text-xs font-black uppercase tracking-[0.12em] text-cyan-100 transition hover:-translate-y-0.5 hover:border-cyan-200/60 hover:bg-cyan-300/15 hover:text-white"
                  >
                    {copiedEmail ? 'Copied' : 'Copy Email'}
                  </button>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                6. Response to Copyright Complaints
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                When we receive a copyright complaint, we may review the report,
                request additional information, remove or disable access to
                related files, suspend processing, restrict account access,
                preserve relevant records, or take other steps we believe are
                appropriate.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                Because uploaded and processed files may be temporary, some
                files may already have expired or been deleted by the time a
                complaint is received. We may still review related processing
                records when available.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                7. Repeat Infringers and Abuse
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                SubtitleTsunami may suspend, restrict, or terminate accounts
                that repeatedly upload unauthorized content, receive repeated
                copyright complaints, attempt to bypass upload confirmations, or
                otherwise misuse the service.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                We may also restrict access to users who create legal,
                operational, security, payment processor, hosting provider, or
                platform risk.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                8. Counter-Notices
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                If your content or account access was restricted because of a
                copyright complaint and you believe the action was a mistake,
                you may contact us with information explaining why you believe
                you had the right to use the content.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                We may review counter-notices in good faith, but we are not
                required to restore access to any file, account, feature, or
                processed output if we believe doing so may create legal,
                payment, infrastructure, or platform risk.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                9. Third-Party Platforms
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                SubtitleTsunami is not affiliated with YouTube, TikTok,
                Instagram, Facebook, X, Vimeo, or other third-party video or
                social platforms unless expressly stated.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                If you upload or use content from a third-party platform, you
                are responsible for complying with that platform’s terms,
                copyright rules, license restrictions, and content policies.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                10. No Legal Advice
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                SubtitleTsunami does not provide legal advice. If you are unsure
                whether you have the right to upload, subtitle, translate,
                process, download, publish, or distribute a video, you should
                seek advice from a qualified attorney before using the service
                for that content.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                11. Changes to This Copyright Policy
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                We may update this Copyright Policy from time to time. When we
                make changes, we may update the effective date above. Continued
                use of SubtitleTsunami after changes are posted means you accept
                the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                12. Contact
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                For copyright questions, copyright complaints, or permission
                issues related to SubtitleTsunami, contact:
              </p>

              <div className="mt-4 rounded-2xl border border-cyan-300/15 bg-slate-950/60 px-4 py-3">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-cyan-300/80">
                  Contact Email
                </p>

                <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                  <p className="break-all text-sm font-extrabold text-cyan-100">
                    {COPYRIGHT_EMAIL}
                  </p>

                  <button
                    type="button"
                    onClick={handleCopyEmail}
                    className="inline-flex w-fit items-center justify-center rounded-xl border border-cyan-300/20 bg-cyan-300/10 px-3 py-1.5 text-xs font-black uppercase tracking-[0.12em] text-cyan-100 transition hover:-translate-y-0.5 hover:border-cyan-200/60 hover:bg-cyan-300/15 hover:text-white"
                  >
                    {copiedEmail ? 'Copied' : 'Copy Email'}
                  </button>
                </div>
              </div>
            </section>
          </div>
        </section>
      </main>

      <KeywordFooter />
    </>
  );
};

export default CopyrightPolicy;